import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
  Divider,
} from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { apiRequest } from "../config/api";

interface PrivacySection {
  title: string;
  content: string[];
}

const PrivacyPolicySettings: React.FC = () => {
  const [sections, setSections] = useState<PrivacySection[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    fetchPrivacyPolicy();
  }, []);

  const fetchPrivacyPolicy = async () => {
    try {
      const response = await apiRequest("/api/public/privacy-policy");
      if (response.ok) {
        const data = await response.json();
        if (data.sections && data.sections.length > 0) {
          setSections(data.sections);
        } else {
          // Default structure
          setSections([
            {
              title: "Privacy Policy",
              content: [
                "This privacy policy explains how we collect, use, and protect your personal information.",
              ],
            },
          ]);
        }
      }
    } catch (err) {
      console.error("Error fetching privacy policy:", err);
      setError("Failed to load privacy policy");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    setError("");

    try {
      const response = await apiRequest("/api/public/privacy-policy", {
        method: "PUT",
        body: JSON.stringify({ sections }),
      });

      if (response.ok) {
        setMessage("Privacy policy saved successfully!");
        setTimeout(() => setMessage(""), 3000);
      } else {
        const data = await response.json();
        setError(data.error || "Failed to save privacy policy");
      }
    } catch (err) {
      console.error("Error saving privacy policy:", err);
      setError("Failed to save privacy policy");
    } finally {
      setSaving(false);
    }
  };

  const addSection = () => {
    setSections([...sections, { title: "", content: [""] }]);
  };

  const removeSection = (index: number) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const updateSectionTitle = (index: number, title: string) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], title };
    setSections(updated);
  };

  const addContentLine = (sectionIndex: number) => {
    const updated = [...sections];
    updated[sectionIndex].content.push("");
    setSections(updated);
  };

  const removeContentLine = (sectionIndex: number, lineIndex: number) => {
    const updated = [...sections];
    updated[sectionIndex].content = updated[sectionIndex].content.filter(
      (_, i) => i !== lineIndex
    );
    setSections(updated);
  };

  const updateContentLine = (
    sectionIndex: number,
    lineIndex: number,
    value: string
  ) => {
    const updated = [...sections];
    updated[sectionIndex].content[lineIndex] = value;
    setSections(updated);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Privacy Policy Settings
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Customize your organization's privacy policy. This will be displayed
          on the public privacy policy page and shown to users during
          registration.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}

        <Stack spacing={3}>
          {sections.map((section, sectionIndex) => (
            <Paper
              key={sectionIndex}
              variant="outlined"
              sx={{ p: 3, position: "relative" }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Section {sectionIndex + 1}
                </Typography>
                <IconButton
                  color="error"
                  onClick={() => removeSection(sectionIndex)}
                  disabled={sections.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              <TextField
                fullWidth
                label="Section Title"
                value={section.title}
                onChange={(e) =>
                  updateSectionTitle(sectionIndex, e.target.value)
                }
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Content Lines:
              </Typography>

              {section.content.map((line, lineIndex) => (
                <Box
                  key={lineIndex}
                  sx={{ display: "flex", gap: 1, mb: 1, alignItems: "start" }}
                >
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={line}
                    onChange={(e) =>
                      updateContentLine(sectionIndex, lineIndex, e.target.value)
                    }
                    placeholder="Enter content paragraph..."
                  />
                  <IconButton
                    color="error"
                    onClick={() =>
                      removeContentLine(sectionIndex, lineIndex)
                    }
                    disabled={section.content.length === 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              <Button
                startIcon={<AddIcon />}
                onClick={() => addContentLine(sectionIndex)}
                sx={{ mt: 1 }}
              >
                Add Content Paragraph
              </Button>
            </Paper>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addSection}
            fullWidth
          >
            Add New Section
          </Button>
        </Stack>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving..." : "Save Privacy Policy"}
          </Button>

          <Button
            variant="outlined"
            onClick={fetchPrivacyPolicy}
            disabled={saving}
          >
            Reset to Saved Version
          </Button>
        </Box>

        {/* Preview */}
        <Paper variant="outlined" sx={{ mt: 3, p: 3, bgcolor: "grey.50" }}>
          <Typography variant="h6" gutterBottom>
            Preview:
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {sections.map((section, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                {section.title || "(Untitled Section)"}
              </Typography>
              {section.content.map((line, lineIndex) => (
                <Typography key={lineIndex} variant="body1" paragraph>
                  {line || "(Empty paragraph)"}
                </Typography>
              ))}
            </Box>
          ))}
        </Paper>
      </Paper>
    </Box>
  );
};

export default PrivacyPolicySettings;
